import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlternatingShowcase } from "~/components/4_modules/AlternatingShowcase";
import { Hero } from "~/components/4_modules/Hero";
import StandardSection from "~/components/4_modules/StandardSection";
import { SEO } from "~/components/SEO";
import { sportsFacilitySchema } from "~/lib/structured-data";

export const Route = createFileRoute("/sport")({
  component: Sport,
});

const sportContent = {
  hero: {
    backgroundImage: "/images/sport-hero.webp",
    title: "Sport & Relax",
  },
  intro: {
    title: "Oltre la tavola",
    paragraph: [
      <span key="paragraph-1">
        Il Tennis & Gourmet non è solo ristorante, <br className="hidden xl:block" /> ma
        anche un centro dedicato al benessere e allo sport.
      </span>,
      <span key="paragraph-2">
        La nostra piscina è un’oasi di relax dove rinfrescarsi durante la bella stagione,
        <br className="hidden xl:block" />
        ideale per famiglie e gruppi di amici.
      </span>,
    ],
  },
};

function Sport() {
  const navigate = useNavigate();

  return (
    <>
      <SEO
        title="Sport & Relax"
        socialTitle="Ristorante Tennis & Gourmet Morbegno | Sport & Relax"
        description="Piscina Morbegno e padel Valtellina al Tennis Gourmet. Centro sportivo con piscina, campi da tennis, padel e calcetto nel cuore della Valtellina. Prenota ora!"
        keywords="piscina morbegno, piscina valtellina, padel valtellina, tennis morbegno, centro sportivo valtellina, padel morbegno, calcetto valtellina"
        url="https://www.ristorantetennisgourmet.it/sport"
        image="/images/sport-hero.webp"
        type="website"
        structuredData={sportsFacilitySchema}
      />
      {/* Hero Section */}
      <Hero backgroundImage={sportContent.hero.backgroundImage}>
        <h2 className="tg-h1 font-medium uppercase">{sportContent.hero.title}</h2>
      </Hero>
      {/* Intro Section */}
      <StandardSection
        title={sportContent.intro.title}
        paragraph={sportContent.intro.paragraph}
        decors={[
          <img
            key="decor-1"
            src="/images/decor/decor-02R.webp"
            alt="page decoration"
            className="absolute right-0 hidden h-auto object-cover xl:top-0 xl:block xl:w-[15vw]"
          />,
        ]}
      ></StandardSection>
      {/* Sport Section */}
      <AlternatingShowcase
        items={[
          {
            id: "piscina",
            title: "Piscina & area relax",
            className: "items-start",
            textColumnClasses: "xl:col-end-8",
            description: (
              <div className="space-y-2">
                <p>
                  Immersa nel verde e nella tranquillità della nostra struttura, la
                  piscina è il luogo perfetto per rinfrescarsi dopo una partita o
                  semplicemente godersi una giornata di relax.
                </p>
                <p>
                  Aperta durante i mesi estivi, è ideale per adulti e bambini grazie alla
                  sua gestione attenta e alla presenza di zone dedicate ai più piccoli, al
                  riposo e al divertimento.
                </p>
                <p>
                  Accanto alla piscina, l’area relax con solarium ti offre lettini e tutto
                  il necessario per staccare la spina, leggere un buon libro o prendere il
                  sole in completo comfort.
                </p>
                <p>
                  Che tu voglia nuotare, rilassarti o semplicemente respirare un po’ di
                  aria buona, la nostra piscina è pronta ad accoglierti!
                </p>
              </div>
            ),
            image: { src: "/images/sport-01.webp", alt: "Piscina", alignment: "bottom" },
            rectangleAlignment: "top",
            cta: {
              text: "Contattaci",
              onClick: () => navigate({ to: "/contact", hash: "contact-form" }),
            },
          },
          {
            id: "campo-da-tennis",
            title: "Campi da tennis in resina (coperti)",
            className: "items-start",
            textColumnClasses: "xl:col-start-1 xl:col-end-7",
            description: (
              <div className="space-y-2 lg:space-y-8">
                <div className="space-y-2">
                  <p>Campi 1, 2 e 3</p>
                  <p>
                    Nuovi, realizzati in resina Mapei di ultima generazione. Perfetti per
                    giocare tutto l’anno, grazie alla copertura che garantisce comfort e
                    protezione in ogni condizione meteo.
                  </p>
                </div>
                <h2 className="tg-h4">Campo da tennis in terra rossa</h2>
                <p>
                  Superficie perfetta per migliorare resistenza, tecnica e gestione del
                  punto. Ammortizza i movimenti e favorisce gli scambi lunghi: l’ideale
                  per chi vuole crescere tatticamente e fisicamente.
                </p>
                <h2 className="tg-h4">Campo polivalente all’aperto</h2>
                <div className="space-y-2">
                  <p>Campo 5</p>
                  <p>
                    Un campo in erba sintetica pensato per il calcetto e il tennis,
                    perfetto per l’attività sportiva all’aria aperta in un contesto
                    rilassante.
                  </p>
                </div>
              </div>
            ),
            image: {
              src: "/images/sport-02.webp",
              alt: "Campo da tennis",
              alignment: "bottom",
            },
            backgroundStyle: "pattern",
            decorPattern: (
              <img
                src="/images/decor/decor-big.webp"
                alt="page decoration"
                className="absolute -right-60 bottom-0 hidden h-full w-auto object-cover xl:block 2xl:-right-124"
              />
            ),
            cta: {
              text: "Prenota online",
              onClick: () => window.open("https://tcmorbegno.wansport.com/", "_blank"),
            },
          },
          {
            id: "campo-da-padel",
            title: "Campi da padel (coperti e riscaldati)",
            className: "items-start",
            textColumnClasses: "xl:col-end-8",
            description: (
              <div className="space-y-2 lg:space-y-8">
                <div className="space-y-2">
                  <p>2 campi coperti</p>
                  <p>
                    Strutture moderne, ideali per giocare anche nei mesi invernali. Il
                    padel è uno sport divertente e coinvolgente, adatto a tutte le età e
                    livelli di esperienza.
                  </p>
                </div>
                <h2 className="tg-h4">Campo da pickleball</h2>
                <p>
                  Una novità assoluta! Il nostro primo campo da pickleball all’aperto,
                  pensato per chi vuole scoprire e praticare uno degli sport più in
                  crescita al mondo.
                </p>
              </div>
            ),
            image: { src: "/images/sport-03.webp", alt: "Elena", alignment: "bottom" },
            rectangleAlignment: "top",
            cta: {
              text: "Prenota online",
              onClick: () => window.open("https://tcmorbegno.wansport.com/", "_blank"),
            },
          },
          {
            id: "biliardo",
            title: "Biliardo",
            className: "items-start",
            textColumnClasses: "xl:col-start-1 xl:col-end-7",
            description: (
              <div className="space-y-2">
                <p>
                  Tavoli professionali sempre a disposizione per sfide tra amici o momenti
                  di relax dopo lo sport.
                </p>
                <p>
                  Un'alternativa perfetta per godersi il tempo al Tennis & Gourmet anche
                  fuori dal ristorante o dal campo.
                </p>
              </div>
            ),
            image: { src: "/images/sport-04.webp", alt: "Biliardo", alignment: "bottom" },
            backgroundStyle: "pattern",
            decorPattern: (
              <img
                src="/images/decor/decor-big.webp"
                alt="page decoration"
                className="absolute -right-60 bottom-0 hidden h-full w-auto object-cover xl:block 2xl:-right-124"
              />
            ),
            cta: {
              text: "Contattaci",
              onClick: () => navigate({ to: "/contact", hash: "contact-form" }),
            },
          },
        ]}
      />
    </>
  );
}
